<template>
  <div>
    <div class="row">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-body">
            <h5 class="mb-5">Request Fund Form</h5>
            <div class="mb-4">
              <label>Account ID <span class="text-danger">*</span></label>
              <div class="form-control p-0"
                :class="{ 'invalid-multiselect': (!$v.request.userId.required || request.userId == 0) && submitted }">
                  <multiselect
                    v-model="request.userId"
                    placeholder="Select Account ID"
                    label="value"
                    track-by="value"
                    :options="userOptions"
                    :multiple="false"
                    :allow-empty="true"
                    :preselect-first="false"
                    @search-change="searchUser"
                    @select="setUserDetails"
                    :loading="isUserOptionsLoading"
                    :internal-search="false"
                  >
                    <template #option="props">
                      <div class="option__desc">
                        <p class="option__title fs-6 mb-1">{{ props.option.value }}</p>
                        <small class="option__small">{{ props.option.businessName }} | {{ props.option.merchantName }}</small>
                      </div>
                    </template>
                  </multiselect>
              </div>
              <p class="small text-danger mb-0" v-if="(!$v.request.userId.required || request.userId == 0) && submitted">Account ID is required</p>
              <b-row class="mt-2">
                <b-col sm="12" md="4" lg="4">
                  <div class="d-flex align-items-center py-0 px-2 rounded personal-details-widget" v-b-tooltip.hover :title="`Business Name: ${user.businessName}`">
                    <i class="mdi text-secondary mdi-storefront-outline me-1"></i>
                    <small class="text-truncate">Business Name: {{ user.businessName }} </small>
                  </div>
                </b-col>
                <b-col sm="12" md="4" lg="4">
                  <div class="d-flex align-items-center py-0 px-2 rounded personal-details-widget" v-b-tooltip.hover :title="`Merchant Name: ${user.merchantName}`">
                    <i class="mdi text-secondary mdi-account-box-outline me-1"></i>
                    <small class="text-truncate">Merchant Name: {{ user.merchantName }} </small>
                  </div>
                </b-col>
                <b-col sm="12" md="4" lg="4">
                  <div class="d-flex align-items-center py-0 px-2 rounded personal-details-widget" v-b-tooltip.hover :title="`Mobile Number: ${user.mobileNumber}`">
                    <i class="mdi text-secondary mdi-cellphone-android me-1"></i>
                    <small class="text-truncate">Mobile Number: {{ user.mobileNumber }} </small>
                  </div>
                </b-col>
              </b-row>
            </div>

            <b-row class="mb-4">
              <b-col sm="12" md="6" lg="6">
                <div>
                  <label>Payment Reference Number<span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="request.referenceNumber"
                    placeholder="Enter Payment Reference Number"
                    maxlength="40"
                    :class="{ 'is-invalid': !$v.request.referenceNumber.required && submitted }"
                  />
                  <p class="small text-danger mb-0" v-if="!$v.request.referenceNumber.required && submitted">
                    Reference Number is required
                  </p>
                </div>
              </b-col>
              <b-col sm="12" md="6" lg="6">
                <div>
                  <label>Deposit Date<span class="text-danger">*</span></label>
                  <flat-pickr
                    v-model="request.depositDate"
                    :config="{
                        maxDate: 'today'
                    }"
                    placeholder="Select Deposit Date"
                    class="form-control w-100"
                    :class="{ 'is-invalid': !$v.request.depositDate.required && submitted }"
                  ></flat-pickr>
                  <p class="small text-danger mb-0" v-if="!$v.request.depositDate.required && submitted">Deposit Date is required</p>
                </div>
              </b-col>
            </b-row>

            <b-row class="mb-4">
              <b-col sm="12" md="6" lg="6">
                <div>
                  <label>Sender Bank<span class="text-danger">*</span></label>
                  <div class="form-control p-0"
                    :class="{ 'invalid-multiselect': (!$v.request.senderBank.required) && submitted }">
                    <multiselect
                      v-model="request.senderBank"
                      placeholder="Select Sender Bank"
                      label="text"
                      track-by="value"
                      :options="senderBankOptions"
                      :multiple="false"
                      :allow-empty="true"
                      :preselect-first="false"
                    >
                    </multiselect>
                  </div>
                  <p class="small text-danger mb-0" v-if="!$v.request.senderBank.required && submitted">Sender Bank is required</p>
                </div>
              </b-col>
              <b-col sm="12" md="6" lg="6">
                <div>
                  <label>Peddlr Bank Account<span class="text-danger">*</span></label>
                  <b-form-select
                    v-if="false"
                    v-model="request.peddlrBank"
                    :options="adjustmentDirectionOptions"
                    class="form-select rounded shadow-none"
                    :class="{ 'is-invalid': !$v.request.peddlrBank.required && submitted }"
                  >
                  </b-form-select>
                  <div class="form-control p-0"
                    :class="{ 'invalid-multiselect': (!$v.request.peddlrBank.required) && submitted }">
                    <multiselect
                      v-model="request.peddlrBank"
                      placeholder="Select Peddlr Bank"
                      label="text"
                      track-by="value"
                      :options="peddlrBankOptions"
                      :multiple="false"
                      :allow-empty="true"
                      :preselect-first="false"
                    >
                    </multiselect>
                  </div>
                  <p class="small text-danger mb-0" v-if="!$v.request.peddlrBank.required && submitted">Peddlr Bank Account is required</p>
                </div>
              </b-col>
            </b-row>

            <b-row class="mb-4">
              <b-col sm="12" md="6" lg="6">
                <div>
                  <label>Deposit Amount<span class="text-danger">*</span></label>
                  <div class="input-group">
                    <span class="input-group-text" id="basic-addon1">₱</span>
                    <input
                      type="number"
                      class="form-control"
                      v-model.number="request.amountPaid"
                      placeholder="0"
                      :class="{ 'is-invalid': (!$v.request.amountPaid.required || request.amountPaid == 0) && submitted }"
                      @input="setTopupAmount"
                      @focus="$event.target.select()"
                    />
                  </div>
                  <p class="small text-danger mb-0" v-if="(!$v.request.amountPaid.required || request.amountPaid == 0) && submitted">Deposit Amount is required</p>
                </div>
              </b-col>
              <b-col sm="12" md="6" lg="6">
                <div>
                  <label>Default Rebate<span class="text-danger">*</span></label>
                  <div class="input-group">
                    <span class="input-group-text" id="basic-addon1">%</span>
                    <input
                      type="number"
                      class="form-control"
                      v-model.number="request.defaultRebate"
                      @input="setTopupAmount"
                      @focus="$event.target.select()"
                      min="0"
                    />
                  </div>
                </div>
              </b-col>
              <p class="text-success mb-0 mt-1">Topup Amount: ₱ {{ request.topupAmount | numberWithCommas }}</p>
            </b-row>

            <b-row class="mb-4">
              <b-col>
                <div>
                  <label>Remarks Purpose <span class="text-muted">(optional)</span></label>
                  <b-form-textarea
                    v-model="request.description"
                    placeholder="Enter remarks purpose"
                    rows="2"
                    max-rows="4"
                    maxlength="255"
                  ></b-form-textarea>
                </div>
              </b-col>
            </b-row>

            <b-row class="mb-5">
              <b-col>
                <div>
                  <label>Deposit Screenshot<span class="text-danger">*</span></label>
                  <b-form-file
                    class="form-control mb-1"
                    :class="{ 'is-invalid': !$v.file.required && submitted }"
                    type="file"
                    accept="image/jpeg, image/png"
                    v-model="file"
                    @change="onFileChange"
                    plain
                  />
                  <label class="form-label fs-12">File extension: PNG, JPEG format limit size 10mb</label>
                  <p class="small text-danger mb-0" v-if="!$v.file.required && submitted">Deposit Screenshot is required</p>
                </div>
              </b-col>
            </b-row>

            <div class="d-flex align-items-center">
              <div class="ms-auto">
                <button class="btn btn-primary me-3" style="width: 170px" @click="saveRequest()">Save</button>
                <router-link :to="{ name: 'GBITS Topup Requests' }" class="tc-white">
                  <b-button variant="light" class="w-sm" type="button" style="width: 170px">Cancel</b-button>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { layoutMethods, gbitsEloadTopupMethods, gbitsEloadTopupComputed } from "@/state/helpers";
import { required } from "vuelidate/lib/validators";
import Swal from "sweetalert2";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Multiselect from "vue-multiselect";
import shortid from "shortid";

export default {
  page: {
    title: "Eload B2B Request Form",
  },
  validations: {
    request: {
      userId: { required },
      referenceNumber: { required },
      depositDate: { required },
      senderBank: { required },
      peddlrBank: { required },
      amountPaid: { required },
    },
    file: { required },
  },
  data() {
    return {
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 20, 30],
      filter: null,
      filterOn: [],
      orderBy: "ebr.id",
      sort: "DESC",
      sortDesc: false,
      submitted: false,
      isSearching: false,
      request: {
        id: 0,
        userId: 0,
        referenceNumber: "",
        depositDate: "",
        senderBank: "",
        peddlrBank: "",
        amountPaid: null,
        topupAmount: 0,
        defaultRebate: 0,
        description: "",
        file: "",
      },
      search: {
        field: "",
        keyword: "",
      },
      usersList: [],
      banksList: [],
      peddlrBanksList: [],
      depositImg: {
        filename: null,
        type: null,
        base64: null,
      },
      s3_url: null,
      file: null,
      user: {
        merchantName: "--",
        businessName: "--",
        mobileNumber: "--",
      },
      isUserOptionsLoading: false,
    };
  },
  components: {
    flatPickr,
    Multiselect,
  },
  computed: {
    ...gbitsEloadTopupComputed,
    userOptions() {
      return this.eloadB2BUsers.map((user) => {
        return {
          value: user.account_id,
          cognitoSub: user.user_id,
          accountId: user.account_id,
          businessName: user.business_name,
          merchantName: user.merchant_name,
          mobileNumber: user.mobile_number,
          userRebate: user.b2b_rate,
        };
      });
    },
    senderBankOptions() {
      return this.eloadB2BBanks.map((bank) => {
        return {
          text: bank.name,
          value: bank.name,
        };
      });
    },
    peddlrBankOptions() {
      return this.eloadB2BPeddlrBanks.map((bank) => {
        return {
          text: bank,
          value: bank,
        };
      });
    },
  },
  methods: {
    ...layoutMethods,
    ...gbitsEloadTopupMethods,
    async saveRequest() {
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      Swal.fire({
        title: "Are you sure?",
        text: "You will no longer be able to edit this request",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "##804997",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then(async (result) => {
        if (result.isConfirmed) {
          let isImgUploaded = await this.uploadFile();

          if (isImgUploaded) {
            const payload = {
              ...this.request,
              depositImgUrl: this.s3_url,
              userId: this.request.userId.cognitoSub,
              senderBank: this.request.senderBank.value,
              peddlrBank: this.request.peddlrBank.value,
            };
            await this.saveEloadB2BRequest(payload)
              .then((res) => {
                console.log(res)
                let icon = res.status == "ok" ? "success" : "error",
                  title = res.status == "ok" ? "Successfully Requested!" : "Request Failed!",
                  content = res.status == "ok" ? `Your request ${res.message.request_id} has been sent successfully. Thank you for your submission!` : (res.message?.description || res.message || "An unexpected error occurred.");
                Swal.fire(title, content, icon).then((swalRes) => {
                  if (res.status == "ok" && swalRes.isConfirmed) this.$router.push({ name: "GBITS Topup Requests" });
                });
              })
              .catch((err) => {
                const errMsg = err?.response?.data?.message || err?.message?.description || err?.message || err || "An unexpected error occurred.";
                Swal.fire("Request Failed!", errMsg, "error");
              });
          }
        }
      });
    },

    async searchUser(keyword) {
      let timeoutId = 0;
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(async () => {
        if (keyword && keyword.length > 2) {
            this.isUserOptionsLoading = true;
            await this.getEloadB2BUsers({ keyword }).then(() => {
                this.isUserOptionsLoading = false;
            })
        }
      }, 1000);
    },

    setUserDetails(user) {
      this.user.merchantName = user.merchantName;
      this.user.businessName = user.businessName;
      this.user.mobileNumber = user.mobileNumber;
      this.request.accountId = user.accountId;
      this.request.defaultRebate = user.userRebate ?? this.eloadB2BDefaultRebate ?? 0;
    },

    setTopupAmount() {
        const paid = parseFloat(this.request.amountPaid) || 0;
        const rebate = parseFloat(this.request.defaultRebate) || 0;
        this.request.topupAmount = Math.round((paid + (paid * rebate / 100)) * 100) / 100;
    },

    onFileChange(e) {
      const reader = new FileReader();
      var files = e.target.files || e.dataTransfer.files;

      if (!files.length) {
        this.depositImg = {
          filename: null,
          type: null,
          base64: null,
        };
        return;
      }

      reader.onloadend = () => {
        var base64Data = reader.result;
        this.depositImg.base64 = new Buffer.from(base64Data.replace(/^data:image\/[a-zA-Z+]+;base64,/, ""), "base64");
        this.depositImg.type = base64Data.split(";")[0].split("/")[1];
        // console.log(">>> base64Data:",reader);

        this.depositImg.filename = `${shortid.generate()}.${this.depositImg.type}`;
        if (this.compareStr(this.file.type, ["csv", "xlsx", "svg", "vnd.openxmlformats-officedocument.spreadsheetml.sheet"]) === true) {
          //"jpeg","png","svg","jpg",
          this.depositImg = {
            filename: null,
            type: null,
            base64: null,
          };
          Swal.fire("Invalid supporting document!", `jpeg or png is required`, "error");
        }
      };
      reader.readAsDataURL(files[0]);
    },

    compareStr(data, arr_similar) {
      // case insensitive string comparison
      for (var i = 0; i < arr_similar.length; i++) {
        var areEqual = data.toUpperCase() === arr_similar[i].toUpperCase();
        if (areEqual) {
          return areEqual;
        }
      }
      return false;
    },

    async uploadFile() {
      let isUploaded = false;
      if (this.depositImg.base64) {
        var depositImg = { ...this.depositImg, accountId: this.request.userId?.accountId || this.request.accountId };

        await this.uploadEloadB2BDepositImg(depositImg)
          .then((imageKey) => {
            this.s3_url = imageKey;
            isUploaded = true;
          })
          .catch((err) => {
            Swal.fire("Upload Failed!", `${err}`, "error");
          });
      } 

      return isUploaded;
    },
  },
  async mounted() {
    this.changeTitle({ title: "Topup Request Form" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: "Peddlr", to: { name: "Home" } },
        { text: "GBITS Manager", to: { name: "" } },
        { text: "Request", to: { name: "GBITS Topup Requests" } },
        { text: "Request Form", to: { name: "" } },
      ],
    });
    try {
      await this.getEloadB2BUsers();
      await this.getEloadB2BBanks();
    } catch (e) {
      console.log("Error loading form data:", e);
    }
  },
};
</script>

<style>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

a.disabled {
  pointer-events: none;
}

.personal-details-widget {
  background-color: #e6eaf1;
}

.multiselect.invalid .multiselect__tags {
  border-color: #f34e4e;
  padding-right: calc(1.5em + 0.94rem);
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23f34e4e'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23f34e4e' stroke='none'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.235rem) center;
  background-size: calc(0.75em + 0.47rem) calc(0.75em + 0.47rem);
}

.invalid ~ .invalid-feedback {
  display: block;
}

.multiselect__option--highlight,
.multiselect__option--highlight::after {
  background: #f8f9fa;
  color: #495057;
}

.multiselect__tag {
  background-color: #e73092 !important;
  border-color: #e73092 !important;
  margin-bottom: 0 !important;
  font-weight: 400 !important;
}

.multiselect__tag-icon:after {
  color: #fff !important;
}

.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
  background-color: #e73092 !important;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.invalid-multiselect  {
  border: 1px solid #f34e4e;
}
</style>
